/**
 * GiveNowModal
 *
 * Full Stripe Payment Element checkout overlay.
 * Supports one-time gifts and recurring monthly sponsorships.
 *
 * Flow:
 *   1. User picks amount + frequency
 *   2. On "Continue" → createPaymentIntent mutation → get client_secret
 *   3. Mount Stripe <PaymentElement> with client_secret
 *   4. On submit → stripe.confirmPayment()
 *   5. Show success screen with shareable link
 */
import { useState } from 'react';
import { loadStripe } from '@stripe/stripe-js';
import { Elements, PaymentElement, useStripe, useElements } from '@stripe/react-stripe-js';
import { useMapStore } from '@/stores/mapStore';
import { useCreatePaymentIntent } from '@/hooks/useCreatePaymentIntent';
import { DONATION_PRESETS } from '@/lib/constants';
import { cn, formatCurrency } from '@/lib/utils';

const stripePromise = loadStripe(import.meta.env.VITE_STRIPE_PUBLISHABLE_KEY);

// ─── Appearance ──────────────────────────────────────────────────────────────
const stripeAppearance = {
  theme: 'night',
  variables: {
    colorPrimary: '#f97316',
    colorBackground: '#1e293b',
    colorText: '#e2e8f0',
    colorDanger: '#f87171',
    fontFamily: 'Inter, system-ui, sans-serif',
    borderRadius: '10px',
  },
};

// ─── Inner form (must be inside <Elements>) ───────────────────────────────────
function CheckoutForm({ amountCents, recurring, onSuccess }) {
  const stripe = useStripe();
  const elements = useElements();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!stripe || !elements) return;

    setIsSubmitting(true);
    setErrorMessage('');

    const { error } = await stripe.confirmPayment({
      elements,
      confirmParams: {
        // Return URL for 3DS redirects — in production use a real page
        return_url: window.location.origin + '/give/confirm',
      },
      redirect: 'if_required',
    });

    if (error) {
      setErrorMessage(error.message || 'Payment failed. Please try again.');
      setIsSubmitting(false);
    } else {
      onSuccess();
    }
  };

  return (
    <form onSubmit={handleSubmit}>
      <PaymentElement className="mb-6" />

      {errorMessage && (
        <div className="mb-4 rounded-lg bg-red-900/40 px-4 py-3 text-sm text-red-300 border border-red-700">
          {errorMessage}
        </div>
      )}

      <button
        type="submit"
        disabled={!stripe || isSubmitting}
        className="w-full rounded-xl bg-brand-500 py-3 font-semibold text-white transition hover:bg-brand-400 disabled:opacity-60"
      >
        {isSubmitting
          ? 'Processing…'
          : `Give ${formatCurrency(amountCents)}${recurring ? '/month' : ''}`}
      </button>
    </form>
  );
}

// ─── Main modal ───────────────────────────────────────────────────────────────
export default function GiveNowModal() {
  const { isGiveModalOpen, giveContext, closeGiveModal } = useMapStore();
  const createIntent = useCreatePaymentIntent();

  const [step, setStep] = useState('select'); // 'select' | 'checkout' | 'success'
  const [selectedPreset, setSelectedPreset] = useState(50);
  const [customAmount, setCustomAmount] = useState('');
  const [recurring, setRecurring] = useState(false);
  const [clientSecret, setClientSecret] = useState('');

  if (!isGiveModalOpen) return null;

  const amountDollars = customAmount ? parseFloat(customAmount) : selectedPreset;
  const amountCents = Math.round(amountDollars * 100);

  const handleContinue = async () => {
    if (amountCents < 100) return; // minimum $1
    try {
      const result = await createIntent.mutateAsync({
        amountCents,
        recurring,
        catalystId: giveContext?.catalystId,
      });
      setClientSecret(result.client_secret);
      setStep('checkout');
    } catch {
      // Error shown via react-query
    }
  };

  const handleSuccess = () => setStep('success');

  const handleClose = () => {
    closeGiveModal();
    // Reset after animation
    setTimeout(() => {
      setStep('select');
      setCustomAmount('');
      setSelectedPreset(50);
      setClientSecret('');
    }, 300);
  };

  const shareUrl = giveContext?.catalystId
    ? `${window.location.origin}/catalysts/${giveContext.catalystId}`
    : window.location.origin;

  return (
    /* Backdrop */
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm animate-fade-in">
      <div className="w-full max-w-md rounded-2xl border border-slate-700 bg-impact-overlay shadow-2xl">
        {/* ── Header ──────────────────────────────────────────────────────── */}
        <div className="flex items-center justify-between border-b border-slate-700 px-6 py-4">
          <div>
            <h2 className="font-display text-xl font-semibold text-slate-100">
              {step === 'success' ? '🙏 Thank You!' : 'Give Now'}
            </h2>
            {giveContext?.regionName && step !== 'success' && (
              <p className="text-xs text-slate-400">Supporting movement in {giveContext.regionName}</p>
            )}
          </div>
          <button
            onClick={handleClose}
            className="rounded-lg p-1.5 text-slate-400 hover:bg-slate-700 hover:text-slate-100"
          >
            <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* ── Body ─────────────────────────────────────────────────────────── */}
        <div className="px-6 py-6">
          {/* Step 1: Amount Selection */}
          {step === 'select' && (
            <div className="space-y-5">
              {/* Frequency toggle */}
              <div className="flex rounded-xl bg-slate-800 p-1">
                {[false, true].map((isRecurring) => (
                  <button
                    key={String(isRecurring)}
                    onClick={() => setRecurring(isRecurring)}
                    className={cn(
                      'flex-1 rounded-lg py-2 text-sm font-medium transition',
                      recurring === isRecurring
                        ? 'bg-brand-500 text-white shadow'
                        : 'text-slate-400 hover:text-slate-200'
                    )}
                  >
                    {isRecurring ? 'Monthly' : 'One-time'}
                  </button>
                ))}
              </div>

              {/* Preset amounts */}
              <div className="grid grid-cols-3 gap-2">
                {DONATION_PRESETS.map((preset) => (
                  <button
                    key={preset}
                    onClick={() => { setSelectedPreset(preset); setCustomAmount(''); }}
                    className={cn(
                      'rounded-xl py-3 text-sm font-semibold transition',
                      selectedPreset === preset && !customAmount
                        ? 'bg-brand-500 text-white'
                        : 'bg-slate-800 text-slate-300 hover:bg-slate-700'
                    )}
                  >
                    ${preset}
                  </button>
                ))}
              </div>

              {/* Custom amount */}
              <div>
                <label className="mb-1.5 block text-xs font-medium text-slate-400">
                  Custom amount (USD)
                </label>
                <div className="flex items-center rounded-xl border border-slate-600 bg-slate-800 px-3 py-2.5 focus-within:border-brand-500 transition">
                  <span className="mr-2 text-slate-400">$</span>
                  <input
                    type="number"
                    min="1"
                    step="1"
                    value={customAmount}
                    onChange={(e) => { setCustomAmount(e.target.value); setSelectedPreset(null); }}
                    placeholder="Enter amount"
                    className="flex-1 bg-transparent text-sm text-slate-100 placeholder-slate-500 focus:outline-none"
                  />
                </div>
              </div>

              {/* Summary */}
              {amountDollars >= 1 && (
                <div className="rounded-xl bg-brand-500/10 px-4 py-3 text-sm text-brand-300 border border-brand-500/20">
                  You'll give{' '}
                  <strong>${amountDollars}</strong>
                  {recurring ? ' every month' : ' one time'}.
                </div>
              )}

              <button
                onClick={handleContinue}
                disabled={amountCents < 100 || createIntent.isPending}
                className="w-full rounded-xl bg-brand-500 py-3 font-semibold text-white transition hover:bg-brand-400 disabled:opacity-50"
              >
                {createIntent.isPending ? 'Loading…' : 'Continue to Payment'}
              </button>
            </div>
          )}

          {/* Step 2: Stripe Checkout */}
          {step === 'checkout' && clientSecret && (
            <Elements
              stripe={stripePromise}
              options={{ clientSecret, appearance: stripeAppearance }}
            >
              <CheckoutForm
                amountCents={amountCents}
                recurring={recurring}
                onSuccess={handleSuccess}
              />
            </Elements>
          )}

          {/* Step 3: Success */}
          {step === 'success' && (
            <div className="space-y-5 text-center">
              <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-green-500/20">
                <svg className="h-8 w-8 text-green-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                </svg>
              </div>
              <div>
                <p className="text-slate-300 text-sm leading-relaxed">
                  Your gift of <strong className="text-slate-100">${amountDollars}</strong>
                  {recurring ? '/month ' : ' '}
                  is sowing into the global harvest.
                </p>
                {giveContext?.regionName && (
                  <p className="mt-1 text-xs text-slate-500">
                    Supporting {giveContext.regionName}
                  </p>
                )}
              </div>

              {/* Shareable link */}
              <div>
                <p className="mb-2 text-xs text-slate-400">Share this movement:</p>
                <div className="flex rounded-xl border border-slate-600 bg-slate-800 overflow-hidden">
                  <input
                    readOnly
                    value={shareUrl}
                    className="flex-1 bg-transparent px-3 py-2 text-xs text-slate-300 focus:outline-none"
                  />
                  <button
                    onClick={() => navigator.clipboard.writeText(shareUrl)}
                    className="px-3 text-xs font-medium text-brand-400 hover:text-brand-300 transition"
                  >
                    Copy
                  </button>
                </div>
              </div>

              <button
                onClick={handleClose}
                className="w-full rounded-xl bg-slate-700 py-2.5 text-sm font-medium text-slate-200 transition hover:bg-slate-600"
              >
                Return to Map
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
